import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, message, Spin } from "antd";
import { useMemo, useState } from "react";
import { getSmsLogs, sendSms } from "@/modules/sms/services/sms.api";
import { mapBackendLogToHistoryItem } from "@/modules/sms/services/sms.mapper";
import type { AppRole } from "@/shared/config/navigation";
import { canSendManualSms } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import type { ClientDetail } from "../types/clients.types";

interface ClientSmsTabProps {
	client: ClientDetail;
	role: AppRole;
}

export function ClientSmsTab({ client, role }: ClientSmsTabProps) {
	const queryClient = useQueryClient();
	const [smsText, setSmsText] = useState("");
	const canSendSms = canSendManualSms(role);

	// 1. Fetch SMS history for this client
	const { data: logsData, isLoading } = useQuery({
		queryKey: ["clientSmsLogs", client.id],
		queryFn: () => getSmsLogs({ clientId: client.id, limit: 100 }),
	});

	const smsHistory = useMemo(() => {
		const logsList = logsData?.items ?? [];
		return logsList.map(mapBackendLogToHistoryItem);
	}, [logsData]);

	// 2. Send SMS mutation
	const sendSmsMutation = useMutation({
		mutationFn: (payload: { clientId: string; messageBody: string }) => sendSms(payload),
		onSuccess: () => {
			message.success("SMS jo'natildi!");
			setSmsText("");
			queryClient.invalidateQueries({ queryKey: ["clientSmsLogs", client.id] });
		},
		onError: (err) => {
			message.error(parseApiError(err) || "SMS yuborishda xatolik yuz berdi");
		},
	});

	const handleSendSms = () => {
		if (!canSendSms) {
			message.warning("Super Admin qo'lda SMS yubormaydi.");
			return;
		}

		if (!smsText.trim()) {
			message.warning("Iltimos, xabar matnini kiriting!");
			return;
		}

		sendSmsMutation.mutate({
			clientId: client.id,
			messageBody: smsText,
		});
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case "delivered":
				return <span className="crm-badge green">Yetkazildi</span>;
			case "failed":
				return <span className="crm-badge red">Xatolik</span>;
			default:
				return <span className="crm-badge slate">Kutilmoqda</span>;
		}
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
			{canSendSms ? (
				<div className="crm-card">
					<div className="crm-card-head">
						<h3 className="font-bold">SMS yuborish</h3>
					</div>
					<div className="crm-card-body">
						<div className="crm-field">
							<span>Mijoz raqami</span>
							<Input value={`+998 ${client.phone}`} disabled style={{ width: "100%" }} />
						</div>
						<div className="crm-field" style={{ marginTop: "12px" }}>
							<span>Xabar matni</span>
							<Input.TextArea
								rows={2}
								placeholder="Xabar matnini kiriting..."
								value={smsText}
								onChange={(e) => setSmsText(e.target.value)}
							/>
						</div>
						<div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
							<Button
								className="crm-primary-btn"
								loading={sendSmsMutation.isPending}
								onClick={handleSendSms}
							>
								Yuborish
							</Button>
						</div>
					</div>
				</div>
			) : null}

			<div className="crm-card crm-sms-tab-table-card">
				<div className="crm-card-head">
					<h3 className="font-bold">SMS tarixi</h3>
				</div>
				<div className="crm-table-wrap">
					{isLoading ? (
						<div style={{ textAlign: "center", padding: "20px" }}>
							<Spin />
						</div>
					) : (
						<table className="crm-table">
							<thead>
								<tr>
									<th>Vaqt</th>
									<th>Xabar</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{smsHistory.length === 0 ? (
									<tr>
										<td colSpan={3} style={{ textAlign: "center", padding: "20px" }}>
											<span className="crm-user-role">SMS tarixi mavjud emas</span>
										</td>
									</tr>
								) : (
									smsHistory.map((sms) => (
										<tr key={`${sms.time}-${sms.message}`}>
											<td style={{ whiteSpace: "nowrap" }} data-label="Vaqt">
												{sms.time}
											</td>
											<td data-label="Xabar">{sms.message}</td>
											<td data-label="Status">{getStatusBadge(sms.status)}</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);
}
